import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Calendar, Clock, User, ArrowRight, BookOpen, TrendingUp, Sparkles, FileText, Newspaper, Package, Layers, Library } from 'lucide-react';
import { Button } from '../ui/button';
import { getApiUrl, getApiHeaders } from '../../utils/apiConfig';

const iconMap: { [key: string]: any } = {
  BookOpen, FileText, Newspaper, Package, Layers, Library, ArrowRight, Clock, Sparkles
};

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  author: string;
  image: string;
  content: string;
  status: 'published' | 'draft';
  createdAt: string;
  updatedAt: string;
}

interface BlogPreviewSection {
  id: string;
  badgeText: string;
  badgeIcon: string;
  title: string;
  description: string;
  ctaText: string;
  ctaLink: string;
  numberOfPosts: number;
  visible: boolean;
  updatedAt: string;
}

// ============================================
// SKELETON
// ============================================

const BlogPreviewSkeleton = () => (
  <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
    <div className="max-w-6xl mx-auto space-y-12">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex justify-center">
          <div className="h-8 w-40 bg-gray-200 rounded-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
        <div className="h-8 bg-gray-200 rounded w-96 mx-auto relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
        <div className="space-y-2 max-w-xl mx-auto">
          <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-4/5 mx-auto relative overflow-hidden">
            <div className="absolute inset-0 shimmer" />
          </div>
        </div>
      </div>

      {/* Blog cards grid — 3 cols matching default numberOfPosts */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="bg-white/80 rounded-2xl overflow-hidden shadow-lg border border-white/60">
            {/* Thumbnail */}
            <div className="aspect-video bg-gray-200 relative overflow-hidden">
              <div className="absolute inset-0 shimmer" />
            </div>
            {/* Content */}
            <div className="p-6 space-y-4">
              {/* Read time badge */}
              <div className="h-6 w-20 bg-gray-200 rounded-full relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
              {/* Title */}
              <div className="space-y-2">
                <div className="h-5 bg-gray-200 rounded w-full relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-5 bg-gray-200 rounded w-4/5 relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              {/* Excerpt */}
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-full relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
                <div className="h-4 bg-gray-200 rounded w-3/4 relative overflow-hidden">
                  <div className="absolute inset-0 shimmer" />
                </div>
              </div>
              {/* CTA link */}
              <div className="h-4 bg-gray-200 rounded w-24 relative overflow-hidden">
                <div className="absolute inset-0 shimmer" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* CTA button */}
      <div className="flex justify-center">
        <div className="h-12 w-44 bg-gray-200 rounded-full relative overflow-hidden">
          <div className="absolute inset-0 shimmer" />
        </div>
      </div>
    </div>
  </section>
);

// ============================================
// COMPONENT
// ============================================

export function BlogPreview() {
  const [sectionData, setSectionData] = useState<BlogPreviewSection | null>(null);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlogSection();
    fetchLatestPosts();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchBlogSection();
        fetchLatestPosts();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const fetchBlogSection = async () => {
    try {
      const timestamp = new Date().getTime();
      const url = getApiUrl(`homepage/blog-preview?t=${timestamp}`);
      const response = await fetch(url, {
        headers: getApiHeaders(),
        cache: 'no-store',
      });
      if (!response.ok) return;
      const result = await response.json();
      if (result.success && result.data) {
        setSectionData(result.data);
      }
    } catch (error: any) {}
  };

  const fetchLatestPosts = async () => {
    try {
      const timestamp = new Date().getTime();
      const response = await fetch(
        getApiUrl(`blogs?t=${timestamp}`),
        { headers: getApiHeaders(), cache: 'no-store' }
      );
      if (!response.ok) return;
      const result = await response.json();
      if (result.success && result.data) {
        const publishedPosts = result.data.filter((post: BlogPost) => post.status === 'published');
        const sortedPosts = publishedPosts.sort((a: BlogPost, b: BlogPost) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setBlogPosts(sortedPosts);
      }
    } catch (error: any) {
    } finally {
      setLoading(false);
    }
  };

  const badgeText   = sectionData?.badgeText    || 'Industry Insights';
  const badgeIcon   = sectionData?.badgeIcon    || 'BookOpen';
  const title       = sectionData?.title        || 'Latest Insights & Industry Trends';
  const description = sectionData?.description  || 'Discover valuable tips, trends, and in-depth articles on animation, digital marketing, and creative strategies.';
  const ctaText     = sectionData?.ctaText      || 'View All Articles';
  const ctaLink     = sectionData?.ctaLink      || '/blog';
  const numberOfPosts = sectionData?.numberOfPosts || 3;
  const visible     = sectionData?.visible !== false;

  const BadgeIcon = iconMap[badgeIcon] || BookOpen;

  // Show skeleton while loading
  if (loading) {
    return <BlogPreviewSkeleton />;
  }

  // Don't render if section is hidden
  if (!visible) return null;

  const hasNoPosts = blogPosts.length === 0;

  return (
    <section className="relative py-12 sm:py-16 lg:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
      {/* Premium Matte Background with Gradient Overlays */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-gradient-to-br from-[#2F2E5C] via-[#4a487a] to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-gradient-to-tl from-[#E87D3E] via-[#E8B84D] to-transparent rounded-full blur-3xl" />
      </div>

      {/* Subtle Matte Texture Overlay */}
      <div className="absolute inset-0 opacity-[0.01]" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")'}} />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-xl px-4 py-2 rounded-full mb-4 border border-white/40 shadow-lg">
            <BadgeIcon className="w-4 h-4 text-[#E87D3E]" />
            <span className="text-sm font-semibold bg-gradient-to-r from-[#E87D3E] to-[#E8B84D] bg-clip-text text-transparent">
              {badgeText}
            </span>
          </div>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 bg-gradient-to-r from-[#2F2E5C] via-[#2F2E5C] to-[#4a487a] bg-clip-text text-transparent">
            {title}
          </h2>
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            {description}
          </p>
        </div>

        {hasNoPosts ? (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto bg-white/80 backdrop-blur-sm rounded-2xl p-8 border border-white/60 shadow-lg">
              <BookOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="font-bold text-lg text-[#2F2E5C] mb-2">No Blog Posts Yet</h3>
              <p className="text-sm text-gray-600">
                Published blog posts will automatically appear here. Create your first blog post in the admin dashboard to get started!
              </p>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
            {blogPosts.slice(0, numberOfPosts).map((blog, index) => {
              const gradients = [
                { bg: "from-[#2F2E5C] to-[#4a487a]", text: "from-[#2F2E5C] to-[#4a487a]" },
                { bg: "from-[#D74444] to-[#e85e5e]", text: "from-[#D74444] to-[#e85e5e]" },
                { bg: "from-[#E87D3E] to-[#E8B84D]", text: "from-[#E87D3E] to-[#E8B84D]" }
              ];
              const gradient = gradients[index % 3];

              return (
                <Link key={blog.id} to={`/blog/${blog.slug}`} className="group">
                  <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border border-white/60 hover:shadow-2xl hover:scale-105 transition-all duration-300">
                    <div className={`absolute inset-0 bg-gradient-to-br ${gradient.bg} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                    <div className="absolute inset-0 opacity-[0.02]" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, #2F2E5C 1px, transparent 0)', backgroundSize: '30px 30px'}} />

                    {/* Featured image */}
                    <div className="relative aspect-video bg-gradient-to-br from-[#2F2E5C] to-[#1a1935] overflow-hidden">
                      {blog.image ? (
                        <img
                          src={blog.image}
                          alt={blog.title}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-center p-8">
                            <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-gradient-to-br from-[#E87D3E] to-[#E8B84D] flex items-center justify-center">
                              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                              </svg>
                            </div>
                            <h3 className="text-white font-bold text-lg mb-1">{blog.category}</h3>
                            <p className="text-white/60 text-sm">Industry Insights</p>
                          </div>
                          <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px'}} />
                        </div>
                      )}

                      {/* Category badge */}
                      <div className="absolute top-4 left-4 z-10">
                        <div className="bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/40 shadow-lg">
                          <span className={`text-xs font-bold bg-gradient-to-r ${gradient.text} bg-clip-text text-transparent`}>
                            {blog.category}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="relative p-6">
                      <div className="flex items-center gap-3 text-xs mb-3">
                        <span className="flex items-center gap-1.5 text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full">
                          <Clock className="w-3.5 h-3.5" />
                          {blog.readTime}
                        </span>
                      </div>

                      <h3 className="font-bold text-base mb-3 text-[#2F2E5C] group-hover:bg-gradient-to-r group-hover:from-[#2F2E5C] group-hover:to-[#4a487a] group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300 line-clamp-2">
                        {blog.title}
                      </h3>

                      <p className="text-sm text-gray-600 mb-4 leading-relaxed line-clamp-3">{blog.excerpt}</p>

                      <div className={`inline-flex items-center font-bold gap-2 text-sm group-hover:gap-3 transition-all duration-300 bg-gradient-to-r ${gradient.text} bg-clip-text text-transparent`}>
                        Read More
                        <ArrowRight className="w-4 h-4 text-[#D74444] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA Button */}
        <div className="text-center mt-10">
          <Link to={ctaLink}>
            <Button className="bg-gradient-to-r from-[#2F2E5C] to-[#4a487a] hover:from-[#1a1935] hover:to-[#2F2E5C] text-white rounded-full px-8 h-12 gap-2 shadow-xl group transition-all duration-300" variant={undefined}>
              {ctaText}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}